import { memo, useState, useCallback } from "react";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { loadedImages } from "./LazyImage";
import PrepBadge, { hasPrepService } from "./PrepBadge";
import ProductCardRating from "../Reviews/ProductCardRating";

const currencyFormatter = new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const BRAND_GREEN = "#469b12";
const CTA_GREEN = "#557c2e";

const ProductCard = ({
  product,
  isInWishlist,
  onShowModal,
  onToggleFill,
  onIncreaseQuantity,
  onDecreaseQuantity,
  isHorizontal = false,
  isMobile = false,
  minimal = false,
  variant = "modern",
  imageHeight,
  styleOverrides = {},
  cardClassName = "",
}) => {
  const [loaded, setLoaded] = useState(
    loadedImages.has(product?.productImage) || false,
  );
  const formatPrice = (value) => currencyFormatter.format(value);

  const isOutOfStock =
    product?.stockLevel === "out_of_stock" || product?.quantityAvailable === 0;

  const showPrepBadge = !minimal && hasPrepService(product);

  const isModern = variant === "modern";
  const ctaHeight = isMobile ? "27px" : "32px";
  const containerStyle = isHorizontal
    ? {
        flex: "0 0 auto",
        width: "21%",
        position: "relative",
      }
    : {};

  const resolvedImageHeight =
    imageHeight ??
    (isModern ? (isMobile ? "120px" : "140px") : isMobile ? "200px" : "250px");
  const resolvedImageHeightPx =
    typeof resolvedImageHeight === "number"
      ? `${resolvedImageHeight}px`
      : resolvedImageHeight;

  const cardStyle = isModern
    ? {
        background: "#fff",
        border: "1px solid #eceef0",
        borderRadius: "15px",
        overflow: "hidden",
        cursor: "pointer",
        ...styleOverrides.card,
      }
    : {
        boxShadow: "0px 4px 4px 0px #00000040",
        cursor: "pointer",
        ...styleOverrides.card,
      };

  const handleCardClick = () => onShowModal(product);
  const stop = (fn) => (e) => {
    e.stopPropagation();
    fn();
  };

  const handleAdd = useCallback(
    () => onIncreaseQuantity(product?.id, 1, true),
    [onIncreaseQuantity, product?.id],
  );

  const handleInc = useCallback(
    () => onIncreaseQuantity(product?.id, 1, false),
    [onIncreaseQuantity, product?.id],
  );

  const handleDec = useCallback(
    () => onDecreaseQuantity(product?.id),
    [onDecreaseQuantity, product?.id],
  );

  const handleToggle = useCallback(
    () => onToggleFill(product?.id),
    [onToggleFill, product?.id],
  );

  return (
    <div
      style={{ ...containerStyle, ...styleOverrides.container }}
      className={isHorizontal ? "mb-3" : "h-100"}
    >
      <Card
        className={`h-100 ${
          isModern ? "product-card-modern" : "border-0"
        } ${cardClassName}`.trim()}
        style={cardStyle}
        onClick={handleCardClick}
      >
        <div className="position-relative" style={{ flex: "none" }}>
          <div
            style={{
              position: "relative",
              height: resolvedImageHeightPx,
            }}
          >
            {!loaded && (
              <Skeleton
                height="100%"
                width="100%"
                style={{ position: "absolute", top: 0, left: 0 }}
              />
            )}
            <Card.Img
              src={product?.productImage}
              alt={product?.productName}
              onLoad={() => {
                setLoaded(true);
                if (product?.productImage)
                  loadedImages.add(product.productImage);
              }}
              style={{
                height: "100%",
                width: "100%",
                objectFit: "cover",
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderTopLeftRadius: isModern ? 0 : undefined,
                borderTopRightRadius: isModern ? 0 : undefined,
                transition: loadedImages.has(product?.productImage)
                  ? "none"
                  : "opacity 0.3s",
                opacity: loaded ? 1 : 0,
                cursor: "pointer",
                ...styleOverrides.image,
              }}
              loading="eager"
              fetchpriority={
                loadedImages.has(product?.productImage) ? "high" : "auto"
              }
            />
          </div>

          {/* OUT OF STOCK OVERLAY */}
          {isOutOfStock && loaded && (
            <Card.ImgOverlay
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.6)",
                height: "100%",
                maxHeight: resolvedImageHeightPx,
              }}
            >
              <div
                className="bg-danger position-absolute d-inline-flex align-items-center justify-content-center"
                style={{
                  borderRadius: "100px",
                  boxShadow: "0px 1px 2px 0px #0000001A",
                  top: "5%",
                  right: "5%",
                  padding: "4px 9px",
                }}
              >
                <span
                  style={{
                    color: "#ffffff",
                    fontSize: "11px",
                    fontWeight: "400",
                    lineHeight: 1,
                  }}
                >
                  OUT OF STOCK
                </span>
              </div>
            </Card.ImgOverlay>
          )}

          {!minimal && !isOutOfStock && loaded && (
            <div
              className="position-absolute d-flex align-items-center justify-content-between"
              style={{ top: "5%", left: "5%", right: "5%", gap: "8px" }}
            >
              {showPrepBadge ? (
                <PrepBadge
                  isMobile={isMobile}
                  style={{ boxShadow: "0px 1px 2px 0px #0000001A" }}
                />
              ) : (
                <span />
              )}

              <div
                className="d-flex text-center align-items-center justify-content-center bg-white flex-shrink-0"
                style={{
                  borderRadius: "50%",
                  width: isMobile ? "24px" : "30px",
                  height: isMobile ? "24px" : "30px",
                  color: "#344054",
                  boxShadow: "0px 1px 2px 0px #0000001A",
                  cursor: "pointer",
                }}
                onClick={stop(handleToggle)}
              >
                <i
                  className={`bi ${
                    isInWishlist ? "bi-heart-fill" : "bi-heart"
                  }`}
                  style={{
                    fontSize: isMobile ? "0.8rem" : "1rem",
                    color: isInWishlist ? BRAND_GREEN : "#c4c9cf",
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <Card.Body
          className="px-2 pt-2 pb-2 d-flex flex-column"
          style={{ flex: "1 1 auto", ...styleOverrides.body }}
        >
          <h1
            style={{
              color: isModern ? "#23272f" : "rgba(53, 53, 66, 0.6)",
              fontWeight: "700",
              display: "flex",
              alignItems: "start",
              justifyContent: "start",
              fontSize: isModern
                ? isMobile
                  ? "13px"
                  : "14px"
                : isMobile
                  ? "15px"
                  : "18px",
              lineHeight: isModern ? 1.3 : undefined,
              ...styleOverrides.name,
            }}
            className="my-0 py-0"
          >
            {product?.productName}
            {product?.size ? ` - ${product?.size}` : ""}
          </h1>

          <ProductCardRating product={product} isMobile={isMobile} />

          <div className="d-flex justify-content-between align-items-center mt-auto">
            {/* PRICES */}
            <div className="d-block gap-2 align-items-center mt-1">
              {product?.discountPrice ? (
                <>
                  <h5
                    className="my-auto"
                    style={{
                      color: "#1D293999",
                      textDecoration: "line-through",
                      fontSize: "16px",
                      fontFamily: "Outfit",
                      fontWeight: "600",
                    }}
                  >
                    {formatPrice(product?.price)}
                  </h5>
                  <h5
                    style={{
                      fontFamily: "Outfit",
                      color: "#353542",
                      fontWeight: isModern ? "800" : "700",
                      fontSize: isModern ? "15px" : undefined,
                      ...styleOverrides.price,
                    }}
                    className="my-auto"
                  >
                    {formatPrice(product?.discountPrice)}
                  </h5>
                </>
              ) : (
                <h5
                  style={{
                    fontFamily: "Outfit",
                    color: "#353542",
                    fontWeight: "700",
                    ...styleOverrides.price,
                  }}
                  className="my-auto"
                >
                  {formatPrice(product?.price)}
                </h5>
              )}
            </div>

            {/* ADD / + / - BUTTONS */}
            {isOutOfStock ? (
              minimal ? null : (
                <i
                  onClick={stop(handleToggle)}
                  className={`bi ${
                    isInWishlist ? "bi-heart-fill" : "bi-heart"
                  }`}
                  style={{
                    fontSize: "1.5rem",
                    color: isInWishlist ? BRAND_GREEN : "black",
                    cursor: "pointer",
                  }}
                />
              )
            ) : (
              <>
                {product.quantity === 0 ? (
                  <Button
                    onClick={stop(handleAdd)}
                    className={`d-flex text-center justify-content-center align-items-center px-3 ${
                      isModern ? "add-btn-modern" : ""
                    }`}
                    style={{
                      height: ctaHeight,
                      fontSize: isMobile ? "12px" : undefined,
                      lineHeight: 1,
                      borderRadius: isModern ? "9px" : undefined,
                      fontWeight: isModern ? "700" : undefined,
                      backgroundColor: isModern ? CTA_GREEN : undefined,
                      borderColor: isModern ? CTA_GREEN : undefined,
                      ...styleOverrides.addButton,
                    }}
                  >
                    ADD
                  </Button>
                ) : (
                  <div
                    className="btn-group btn-group-sm"
                    onClick={(e) => e.stopPropagation()}
                    style={
                      isModern
                        ? {
                            border: `1.5px solid ${BRAND_GREEN}`,
                            borderRadius: "9px",
                            overflow: "hidden",
                          }
                        : undefined
                    }
                  >
                    <Button
                      onClick={handleDec}
                      variant={isModern ? "light" : undefined}
                      className="px-1 py-0 d-flex text-center align-items-center justify-content-center"
                      style={{
                        borderRadius: isModern ? "0" : "3px",
                        borderColor: isModern ? "transparent" : "#B2BCCA",
                        background: isModern ? "#eef3e6" : undefined,
                        color: isModern ? BRAND_GREEN : undefined,
                        width: "1.6rem",
                        height: ctaHeight,
                        fontWeight: "700",
                        ...styleOverrides.qtyButton,
                      }}
                    >
                      -
                    </Button>

                    <Button
                      variant="white"
                      className="px-1 py-0 d-flex text-center align-items-center justify-content-center"
                      style={{
                        width: "1.6rem",
                        height: ctaHeight,
                        fontWeight: "700",
                      }}
                    >
                      {product.quantity}
                    </Button>

                    <Button
                      onClick={handleInc}
                      variant={isModern ? "light" : undefined}
                      className="px-1 py-0 d-flex text-center align-items-center justify-content-center"
                      style={{
                        borderRadius: isModern ? "0" : "3px",
                        borderColor: isModern ? "transparent" : "#B2BCCA",
                        background: isModern ? "#eef3e6" : undefined,
                        color: isModern ? BRAND_GREEN : undefined,
                        width: "1.6rem",
                        height: ctaHeight,
                        fontWeight: "700",
                        ...styleOverrides.qtyButton,
                      }}
                    >
                      +
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default memo(ProductCard, (prev, next) => {
  return (
    prev.product?.id === next.product?.id &&
    prev.product?.quantity === next.product?.quantity &&
    prev.isInWishlist === next.isInWishlist &&
    prev.product?.stockLevel === next.product?.stockLevel &&
    prev.product?.quantityAvailable === next.product?.quantityAvailable &&
    prev.product?.price === next.product?.price &&
    prev.product?.discountPrice === next.product?.discountPrice &&
    hasPrepService(prev.product) === hasPrepService(next.product) &&
    prev.isHorizontal === next.isHorizontal &&
    prev.minimal === next.minimal &&
    prev.variant === next.variant &&
    prev.imageHeight === next.imageHeight
  );
});
